import { ChevronRightIcon, LockIcon, LogOutIcon } from "lucide-react";

import { TraceButton } from "@/components/app/trace/trace-button";
import Link from "next/link";
import type { ReactNode } from "react";

import { ExternalChannelBadge } from "@/components/app/workspace/external-channel-badge";
import { Button } from "@/components/ui/button";
import type { WorkspaceChannel } from "@/lib/mlx-workspace-client";

/*
 * The channel toolbar as markup. ChannelWorkspaceToolbar owns the channel's
 * data, its access query and sheet, and renders its title row and actions
 * through these, so a fixture channel composes the same header.
 */

/** The channel page's title row: "Channel › Activity" on a section's page and "Channel › Data set" inside a data set, with the channel's actions at the end. */
export function ChannelWorkspaceToolbarView({
  channel,
  crumb,
  external = false,
  end,
}: {
  /** Null while the channel loads, when the title reads "Channel". */
  channel: Pick<WorkspaceChannel, "id" | "name" | "purpose"> | null;
  /** The open section or data set, named after the channel. */
  crumb?: { name: string } | null;
  /** Tells a member that guests from outside the organisation can see this channel. */
  external?: boolean;
  end?: ReactNode;
}) {
  return (
    <header className="mlx-page-toolbar">
      <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-2">
          <h1
            className="mlx-page-toolbar-title channel-workspace-toolbar-title shrink-0"
            title={channel?.purpose || channel?.name}
          >
            {channel ? (
              <Link
                href={`/workspace/channels/${encodeURIComponent(channel.id)}`}
                className="block min-w-0 truncate rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                {channel.name}
              </Link>
            ) : (
              "Channel"
            )}
          </h1>
          {external ? <ExternalChannelBadge /> : null}
          {crumb ? (
            <>
              <ChevronRightIcon
                aria-hidden
                className="size-3.5 shrink-0 text-muted-foreground"
              />
              <span
                aria-current="page"
                className="min-w-0 truncate text-sm font-medium"
                title={crumb.name}
              >
                {crumb.name}
              </span>
            </>
          ) : null}
        </div>
        <div className="mlx-page-toolbar-actions channel-workspace-toolbar-actions">
          {end}
        </div>
      </div>
    </header>
  );
}

/** The channel's end actions: the open data set's freshness, a divider, then the channel's own actions. */
export function ChannelWorkspaceToolbarEndView({
  freshness,
  actions,
}: {
  /** Shown only for data with a source; data stored in Twin is always current. */
  freshness?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <>
      {freshness}
      <div
        aria-hidden
        className="channel-workspace-toolbar-divider h-5 w-px shrink-0 bg-border/70"
      />
      {actions}
    </>
  );
}

/** On a dashboard or a data set view: opens the trace of the data behind it, with the app's one trace button. */
export function ChannelTraceButton({
  subject,
  onClick,
}: {
  /** What is traced: "this dashboard's data". */
  subject: string;
  onClick?: () => void;
}) {
  return (
    <TraceButton subject={subject} onClick={onClick} className="px-1.5" labelClassName="channel-workspace-toolbar-action-label" />
  );
}

/** Opens the channel's access settings, for people who can manage them. */
export function ChannelAccessButton({ onClick }: { onClick?: () => void }) {
  return (
    <Button
      size="icon-sm"
      variant="outline"
      aria-label="Channel access"
      title="Channel access"
      onClick={onClick}
    >
      <LockIcon className="size-3.5" />
    </Button>
  );
}

/** Leaves the channel, for a member who does not manage its access. */
export function ChannelLeaveButton({ onClick }: { onClick?: () => void }) {
  return (
    <Button
      size="icon-sm"
      variant="outline"
      aria-label="Leave channel"
      title="Leave channel"
      onClick={onClick}
    >
      <LogOutIcon className="size-3.5" />
    </Button>
  );
}
